import { Injectable } from '@angular/core';
import { ServiceResponse } from '@suite/common/data-models/service/service-response';
import { CollectionRecord } from '@suite/common/data-models/records/collection-record';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { NomWebServicesJson } from '@suite/common/services/nom-web-services-json';
import { GlobalTablesService } from '@suite/common/services/global-tables-service';
import { VisualStateService } from '@suite/common/services/visual-state-service';
import { ServiceCollectionRecord } from '@suite/common/data-models/service/service-collection-record';
import { DataStore } from '@suite/common/data-models/data-store/data-store';
import { RundownRecord } from '../data-models/records/rundown-record';
import { ApiServiceCollectionRecord } from '@suite/common/data-models/service/api-service-collection-record';
import { Util } from "@suite/common/utils/util";
import { ObjectTypes } from '@suite/common/enums/object-types';
import { Record } from '@suite/common/data-models/records/record';
import { NavigationLayoutTypes } from 'suite/common/enums/navigation-layout-types';
import { ILayoutInfo } from '@live/interfaces/i-layout-info';
import { ErrorTypes } from '@suite/common/data-models/misc/errors';
import { Layout } from '@common/data-models/service/layout'
import { ServiceLayout } from '@common/data-models/service/service-layout';

@Injectable({ providedIn: 'root' })
export class RundownService {

    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService
    ) { }

    public async createStory(database: string, path: string, collectionGuid: string, fields: { [id: string]: any }, referal: string): Promise<ServiceResponse<RundownRecord>> {
        let requestParams = {};
        let response: HttpResponse<any> = null;
        Util.escapeSpecialCharsForFileName(fields)
        let fieldsToUpdate = this.nomWebServicesJson.Convertor.toENPSFields(fields);
        requestParams = {
            collectionGuid: collectionGuid,
            collectionPath: path,
            database: database,
            fieldsToUpdate: fieldsToUpdate,
            insertBeforeGuid: "",
            returnText: false
        }
        try {
            response = await this.nomWebServicesJson.patch(
                "/api/RundownContent",
                requestParams,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to create story record!",
                detailMessage: `Failed to create story record! \n\rStackTrace: NomWebServicesJson.createStory(); \n\rError: ${error}`
            });
            return new ServiceResponse<RundownRecord>(false, null);
        }
        let serviceRecord = ServiceCollectionRecord.instance(response.body);
        let converted = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord(serviceRecord);

        await DataStore.setCollection(converted, true);
        return new ServiceResponse<RundownRecord>(true, DataStore.byGuid<RundownRecord>(converted.guid, referal));
    }

    public async getRundown(database: string, path: string, guid: string, referal: string): Promise<ServiceResponse<RundownRecord>> {
        let searchParams = this.nomWebServicesJson.searchParams({
            database: database,
            path: path,
            guid: guid,
            hitHighlightTerm: "",
            returnText: "true"
        });
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.get(
                "/api/RundownContent",
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        database: database,
                        path: path,
                        guid: guid,
                        hitHighlightTerm: "",
                        returnText: "true"
                    },
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load rundown content!",
                detailMessage: `Failed to load rundown content! \n\rStackTrace: NomWebServicesJson.getRundown(); \n\rError: ${error}`
            });
            return new ServiceResponse<RundownRecord>(false, null);
        }
        if (this.nomWebServicesJson.platformConvertor.useFor["getRundown"]) {
            let serviceRecord = ApiServiceCollectionRecord.instance(response.body);
            let converted = this.nomWebServicesJson.platformConvertor.fromServiceCollectionRecord(serviceRecord);

            await DataStore.setCollection(converted, true);
            return new ServiceResponse<RundownRecord>(true, DataStore.byGuid<RundownRecord>(converted.guid, referal));
        } else {
            let serviceRecord = ServiceCollectionRecord.instance(response.body);
            let converted = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord(serviceRecord);

            await DataStore.setCollection(converted, true);
            return new ServiceResponse<RundownRecord>(true, DataStore.byGuid<RundownRecord>(converted.guid, referal));
        }
    }

    public async getList(database: string, path: string): Promise<any> {
        let headers = new HttpHeaders({
            "Content-Type": "application/json",
            "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
        });
        let params = {
            database: database,
            ENPSListParameters: [{
                Path: path,
                Guid: "",
                Type: 0,
                StartTime: "1800-02-28T22:26:44.000Z",
                EndTime: "1800-02-28T22:26:44.000Z",
                Priority:-1,
                UnreadBy: ""
            }]                    
        };
        try {
            return await this.nomWebServicesJson.post(
                "api/Lists", params, { headers ,  responseType: 'json', observe: 'response' });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                title: "ERROR",
                message: "Failed to get list!",
                detailMessage: `Failed to get list! \n\rStackTrace: RundownServices.getList(); \n\rError: ${error}`
            }, ErrorTypes.ERROR);
            return new ServiceResponse<any>(false);
        };
    };

    // get stored layouts
    public async getStoredLayouts(): Promise<any> {
        try {
            return await this.nomWebServicesJson.get(
                "api/Layouts", {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                params: {
                    objectType: ObjectTypes.RUNDOWN
                },
                observe: 'response'
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to get stored layouts!",
                detailMessage: `Failed to get stored layouts! \n\rStackTrace: RundownServices.getStoredLayouts(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
    }

    public async getLayout(type: number, name: string = ''): Promise<any> {
        let params = { LayoutType: type, CollectionType: ObjectTypes.RUNDOWN };
        if (name != '') {
            params['LayoutName'] = name;
        };
        try {
            let ret = await this.nomWebServicesJson.post(
                "api/Layouts", params, {
                headers: new HttpHeaders({
                    "Content-Type": "application/json",
                    "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                }),
                responseType: 'json',
                observe: 'response'
            });
            let serviceRecord: ServiceLayout = ServiceLayout.instance(ret.body, ObjectTypes.RUNDOWN);
            let converted = this.nomWebServicesJson.Convertor.fromServiceLayout({ DefaultLayout: serviceRecord.DefaultLayout, DefaultLayoutType: serviceRecord.DefaultLayoutType });
            return new ServiceResponse<Layout>(true, converted);
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to get a layout!",
                detailMessage: `Failed to get a layout! \n\rStackTrace: RundownServices.getUserLayout(); \n\rError: ${error}`
            });
            return new ServiceResponse<Layout>(false);
        }
    }

    // all above are tested

/*
    public async getRo(): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.get<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl(""), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to get RO!",
                detailMessage: `Failed to get RO! \n\rStackTrace: RundownServices.getRo(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
    }
*/
/*
    public async getRoLockStatus(guid: string, database: string, path: string): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.get<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("RundownLockStatus"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        database: database,
                        guid: guid,
                        path: path
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to get RO lock status!",
                detailMessage: `Failed to get RO lock status! \n\rStackTrace: RundownServices.getRoLockStatus(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
    }

    public async editStoryContent(guid: string, type: string, database: string, path: string, content: any): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.patch<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("BasicContent"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        guid: guid,
                        type: type,
                        database: database,
                        path: path,
                        keepApproved: true,
                        parentGuid: "string",
                        fieldsToUpdate: [
                        {
                            FieldName: "Text",
                            FieldValue: content
                        }
                        ]
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to edit story content!",
                detailMessage: `Failed to edit story content! \n\rStackTrace: RundownServices.editStoryContent(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
    }

    public async editStoryCell(guid: string, type: string, database: string, path: string, fieldName: string, content: string): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.patch<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("BasicContent"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        guid: guid,
                        type: type,
                        database: database,
                        path: path,
                        keepApproved: false,
                        parentGuid: "string",
                        fieldsToUpdate: [
                          {
                            FieldName: fieldName,
                            FieldValue: content
                          }
                        ]
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to edit story cell!",
                detailMessage: `Failed to edit story cell! \n\rStackTrace: RundownServices.editStoryCell(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
    }
    
    public async getStoryContent(guid: string, database: string, path: string): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.patch<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("BasicContent"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        guid: guid,
                        database: database,
                        path: path,
                        hitHighlightTerm: true
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to edit story cell!",
                detailMessage: `Failed to edit story cell! \n\rStackTrace: RundownServices.editStoryCell(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
    }
    
      public async putBasicContent(basicContent: Record): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.put<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("BasicContent"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        basicContent: basicContent
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to put basic content!",
                detailMessage: `Failed to put basic content! \n\rStackTrace: RundownServices.putBasicContent(); \n\rError: ${error}`
            });
            return new ServiceResponse<Record>(false);
        }
        return new ServiceResponse<Record>(true);
      }

    public async putRoContent(database: string, path: string, keepApproved: boolean, templateGuid: string, templateProgramId: string, fields: { [id: string]: any }): Promise<any> {
        let response: HttpResponse<any>;
        let fieldsToUpdate = this.nomWebServicesJson.Convertor.toENPSFields(fields);
        try {
            response = await this.nomWebServicesJson.http.put<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("RundownContent"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        database: database,
                        path: path,
                        keepApproved: keepApproved,
                        templateGuid: templateGuid,
                        templateProgramId: templateProgramId,
                        fieldsToUpdate: fieldsToUpdate
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to put RO content!",
                detailMessage: `Failed to put RO content! \n\rStackTrace: RundownServices.putRoContent(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
      }
    
      public async deleteBasicContent(basicContent: Record, guid: string, database: string, path: string): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.post<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("BasicContent"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        basicContent: basicContent,
                        guid: guid,
                        database: database,
                        path: path
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to delete basic content!",
                detailMessage: `Failed to delete basic content! \n\rStackTrace: RundownServices.deleteBasicContent(); \n\rError: ${error}`
            });
            return new ServiceResponse<Record>(false);
        }
        return new ServiceResponse<Record>(true);
      }

/*    replaced
      // Layouts
      public async getUserLayout(): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.post<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("Layouts"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        LayoutType: NavigationLayoutTypes.User, 
                        CollectionType: ObjectTypes.RUNDOWN
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to get user layout!",
                detailMessage: `Failed to get user layout! \n\rStackTrace: RundownServices.getUserLayout(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
      }
    
      public async getGroupLayout(): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.post<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("Layouts"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        LayoutType: NavigationLayoutTypes.Group, 
                        CollectionType: ObjectTypes.RUNDOWN
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to get group layout!",
                detailMessage: `Failed to get group layout! \n\rStackTrace: RundownServices.getGroupLayout(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
      }
    
      public async getSystemLayout(): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.post<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("Layouts"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        LayoutType: NavigationLayoutTypes.System, 
                        CollectionType: ObjectTypes.RUNDOWN
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to get system layout!",
                detailMessage: `Failed to get system layout! \n\rStackTrace: RundownServices.getSystemLayout(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
      }
    
    
      // get stored layout by name
      public async getStoredLayoutByName(layoutName: string): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.post<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("Layouts"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        LayoutType: NavigationLayoutTypes.Stored, 
                        CollectionType: ObjectTypes.RUNDOWN, 
                        LayoutName: layoutName
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to get stored layouts!",
                detailMessage: `Failed to get stored layouts! \n\rStackTrace: RundownServices.getStoredLayouts(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
      }
*/


/*
      // get all fields from g_files
    public async getDefinedFields(): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.get<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("Layouts/GetAllSystemFieldsByType"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to get defined fiels!",
                detailMessage: `Failed to get defined fiels! \n\rStackTrace: RundownServices.getDefinedFields(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
    }
    
      // save layout
      public async saveLayout(guid: string, database: string, path: string, layoutInfo: ILayoutInfo, listColumns: object[]): Promise<any> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.put<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("Layouts/PutLayout"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        database: database,
                        Guid: guid,
                        Path: path,
                        layoutInfo: layoutInfo,
                        listColumns: listColumns
                      },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to save layout!",
                detailMessage: `Failed to save layout! \n\rStackTrace: RundownServices.saveLayout(); \n\rError: ${error}`
            });
            return new ServiceResponse<any>(false);
        }
        return new ServiceResponse<any>(true);
    }
    
    public async deleteStoredLayout(layoutName: string): Promise<object> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.get<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("Layouts/DeleteLayout"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        LayoutName: layoutName,
                        objectType: ObjectTypes.RUNDOWN
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to delete stored layout!",
                detailMessage: `Failed to delete stored layout! \n\rStackTrace: RundownServices.deleteStoredLayout(); \n\rError: ${error}`
            });
            return new ServiceResponse<object>(false);
        }
        return new ServiceResponse<object>(true);
    }
    
    public async saveUserLayout(gridLayout: any): Promise<object> {
        let response: HttpResponse<any>;
        try {
            response = await this.nomWebServicesJson.http.get<HttpResponse<any>>(
                this.nomWebServicesJson.buildEndpointUrl("Layouts/SaveAsUserLayout"), {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        tableName: "tblRundownLayout",
                        gridLayout: gridLayout
                    },
                    observe: 'response'
                }).toPromise();
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to save user layout!",
                detailMessage: `Failed to save user layout! \n\rStackTrace: RundownServices.saveUserLayout(); \n\rError: ${error}`
            });
            return new ServiceResponse<object>(false);
        }
        return new ServiceResponse<object>(true);
    }
*/
/*************************************************/


} 
